import { NgModule, APP_INITIALIZER, LOCALE_ID } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { WelcomeComponent } from './welcome/welcome.component';
import { environment } from "../environments/environment";

import { NgxFlagIconCssModule } from "ngx-flag-icon-css";
import { registerLocaleData, CommonModule } from "@angular/common";
import localePtPt from "@angular/common/locales/pt-PT";
import localeEs from "@angular/common/locales/es";
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { AppSessionService } from "./shared/services/app-session/app-session.service";
import { AuthGuard, UnsavedChangesGuard } from "./shared";
import { BasicAuthInterceptor } from "./http-component/basic-authInterceptor";
import { OAuthModule } from "angular-oauth2-oidc";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { EffectsModule } from "@ngrx/effects";
import { reducers } from "./store/reducers";
import { LoginEffects } from "./store/effects/login/login.effects";
import { UsersEffects } from "./store/effects/users/user.effects";
import { MessagesEffects } from "./store/effects/messages/message.effects";
import { VehiclesEffects } from "./store/effects/vehicles/vehicles.effects";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { languageLoader } from "./custom-loader/languageLoader";
import { Router } from "@angular/router";
import { CustomersModule } from "./layout-modules/customers-Management/customers.module";
import { PartiesEffects } from "./store/effects/parties/parties.effects";
import { ContractsModule } from "./layout-modules/contracts-Management/contracts.module";
import { ContractsEffects } from "./store/effects/contracts/contracts.effects";
import { CardsEffects } from "./store/effects/cards/cards.effects";
import { PromptsEffects } from "./store/effects/prompts/prompts.effects";
import { PropertiesEffects } from "./store/effects/properties/properties.effects";
import { CategoriesEffects } from "./store/effects/categories/categories.effects";
import { MenusEffects } from "./store/effects/menus/menus.effects";
import { ReportsEffects } from "./store/effects/reports/reports.effects";
import { SitesEffects } from "./store/effects/sites/sites.effects";
import { MovementEffects } from "./store/effects/movements/movements.effects";
import { LanguageSelectModule } from "./shared/modules/language-select/language-select.module";
import { TenantSelectModule } from "./shared/modules/tenant-select/tenant-select.module";
import { FuelStocksEffects } from "./store/effects/fuelStock/fuelStock.effects";
import { DeliveriesEffects } from "./store/effects/deliveries/delivery.effects";
import { WarehouseEffects } from "./store/effects/warehouse/warehouse.effects";
import { NgxEchartsModule } from "ngx-echarts";
import * as echarts from "echarts";
import { DashboardEffects } from "./store/effects/dashboard/dashboard.effects";
import { DatePickerModule } from "./shared/modules/datepicker-range/datepicker-range.module";
import { ProfessionalFuelEffects } from "./store/effects/professional-fuel/professional-fuel.effects";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { ErrorHandler } from "@angular/core";
import { LoggerService } from "./shared/services/logging/logging.service";
import { GlobalErrorHandlerService } from "./shared/services/error-handler/error-handler.service";
import { TenantEffects } from "./store/effects/tenant/tenant.effects";
import { SitesModule } from "./layout-modules/sites-Management/sites.module";
import { GradesEffects } from "./store/effects/grades/grades.effects";
import { ProductModule } from "./layout-modules/products-Management/product.module";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatMenuModule } from "@angular/material/menu";
import { MenuListModule } from "./shared/modules/menu-list/menu-list.module";
import { LoginModule } from "./shared/modules/login/login.module";
import { PasswordRecoveryModule } from "./shared/modules/password-recovery/password-recovery.module";
import { ProductsEffects } from "./store/effects/products/product.effects";
import { ProductFamilyModule } from "./layout-modules/product-family-Management/product-family.module";
import { CategoriesModule } from "./shared/modules/categories/categories.module";
import { PropertiesModule } from "./shared/modules/properties/properties.module";
import { SnackbarModule } from "./shared/modules/snackbar/snackbar.module";
import { MatCardModule } from "@angular/material/card";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatDialogModule } from "@angular/material/dialog";
import { DialogModule } from "./shared/modules/dialogs/dialog.module";
import { ProductGroupEffects } from "./store/effects/productGroups/productGroup.effects";
import { DictionaryEffects } from "./store/effects/dictionary/dictionary.effects";
import { ProductGroupModule } from "./layout-modules/product-group-Management/product-group.module";
import { PosCardsEffects } from "./store/effects/pos-cards/pos-cards.effects";
import { ServiceModeEffects } from "./store/effects/service-modes/service-modes.effects";
import { FuelingModeModule } from "./layout-modules/fueling-mode-Management/fueling-mode.module";
import { FuelingModeEffects } from "./store/effects/fueling-modes/fueling-modes.effects";
import { TankGaugeEffects } from "./store/effects/tanks-gauges/tank-gauges.effects";
import { LocalesEffects } from "./store/effects/locales/locales.effects";
import { DashboardThemeService } from "./shared/services/dashboard-theme/dashboard-theme.service";
import { HomeModule } from "./home/home.module";
import { TranslationLoaderService } from "./shared/services/translations/translation-loader.service";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

registerLocaleData(localePtPt);
registerLocaleData(localeEs);

function appLoadFactory(config: AppSessionService) {
  return () => config.loadConfig().toPromise();
}

function loadTranslationsFactory(translationLoader: TranslationLoaderService) {
  return () => translationLoader.loadTranslations();
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent, WelcomeComponent],
  imports: [
    CommonModule,
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    // LanguageTranslationModule, for some reason if we call this module it does 2 requests for translations
    NgxEchartsModule.forRoot({
      echarts,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    StoreModule.forRoot(reducers),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    EffectsModule.forRoot([
      LoginEffects,
      UsersEffects,
      MessagesEffects,
      PartiesEffects,
      ContractsEffects,
      VehiclesEffects,
      PromptsEffects,
      CardsEffects,
      PropertiesEffects,
      PromptsEffects,
      CategoriesEffects,
      MenusEffects,
      ReportsEffects,
      SitesEffects,
      MovementEffects,
      FuelStocksEffects,
      DeliveriesEffects,
      WarehouseEffects,
      DashboardEffects,
      ProfessionalFuelEffects,
      TenantEffects,
      GradesEffects,
      ProductsEffects,
      ProductGroupEffects,
      PosCardsEffects,
      DictionaryEffects,
      ServiceModeEffects,
      FuelingModeEffects,
      TankGaugeEffects,
      LocalesEffects,
    ]),
    LoggerModule.forRoot({
      serverLoggingUrl: "/api/logs",
      level: NgxLoggerLevel.ERROR,
      serverLogLevel: NgxLoggerLevel.ERROR,
      disableConsoleLogging: false,
      colorScheme: [
        "purple",
        "teal",
        "teal",
        "gray",
        "orange",
        "yellow",
        "red",
      ],
    }),
    ReactiveFormsModule,
    NgxFlagIconCssModule,
    HttpClientModule,
    OAuthModule,
    AppRoutingModule,
    HomeModule,
    CustomersModule,
    DatePickerModule,
    ContractsModule,
    LanguageSelectModule,
    TenantSelectModule,
    SitesModule,
    ProductModule,
    MenuListModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    LoginModule,
    PasswordRecoveryModule,
    ProductFamilyModule,
    CategoriesModule,
    PropertiesModule,
    ProductGroupModule,
    SnackbarModule,
    MatCardModule,
    FlexLayoutModule,
    MatDialogModule,
    DialogModule,
    FuelingModeModule,
  ],
  providers: [
    AuthGuard,
    UnsavedChangesGuard,
    LoggerService,
    DashboardThemeService,
    {
      provide: APP_INITIALIZER,
      useFactory: appLoadFactory,
      deps: [AppSessionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadTranslationsFactory,
      deps: [TranslationLoaderService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: "pt-PT",
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true,
    },
    GlobalErrorHandlerService,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(router: Router) {}
}
